import * as React from "react";
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Modal,
  Box,
  Typography,
  Paper,
  IconButton,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import {
  getReminderTypes,
  resetSellerStatusUpdate,
  SELLER_STATUS_UPDATE_ACTIONS,
  setSelectedStatus,
} from "../../redux/slices/sellerSlice";
import { getDefaultValueStatus } from "../../utils";

const PlaceOnHoldModal = ({

  handlers: { handleConfirm },
  flag,

}) => {

  const dispatch = useDispatch();

  const { selectedSeller, sellerStatusUpdate } = useSelector((state) => state.seller);

  const onClose = () => {
    dispatch(setSelectedStatus({ data: getDefaultValueStatus(selectedSeller?.status_type)}))
  }

  const [reason, setReason] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const state = useSelector(({ seller: { reminderTypes } }) => ({
    reminderTypes,
  }));

  React.useEffect(() => {
    dispatch(getReminderTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!flag) {
      setReason("");
      setComment("");
    }
  }, [flag]);

  const handleClose = () => {
    setReason("");
    setComment("");
    setIsSubmitting(false);
    dispatch(resetSellerStatusUpdate());
    if (onClose) {
      onClose();
    }
  };

  const handleChange = (e) => {
    setReason(e.target.value);
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const handlePlaceOnHold = () => {
    setIsSubmitting(true);
    dispatch(resetSellerStatusUpdate());
    handleConfirm({
      reminder_type_id: `${reason}`,
      reminder_comments: comment,
    });
  };

  React.useEffect(() => {
    if (!isSubmitting) return;
    if (sellerStatusUpdate.actionType !== SELLER_STATUS_UPDATE_ACTIONS.ON_HOLD) {
      return;
    }
    if (sellerStatusUpdate.inProgress) return;

    if (sellerStatusUpdate.isSuccess) {
      setReason("");
      setComment("");
      setIsSubmitting(false);
      dispatch(resetSellerStatusUpdate());
      dispatch(setSelectedStatus({ data: getDefaultValueStatus(selectedSeller?.status_type) }));
      return;
    }

    if (sellerStatusUpdate.isError) {
      setIsSubmitting(false);
    }
  }, [dispatch, isSubmitting, selectedSeller?.status_type, sellerStatusUpdate]);

  return (
    <Modal
      open={!!flag}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: "960px",
          maxHeight: "920px",
          overflowY: "auto",
          borderRadius: "16px",
          p: '24px',
          position: "relative",
          backgroundColor: "#ffffff",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "#000",
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: "24px",
            mb: "24px",
            color: "#000",
            pr: 3,
          }}
        >
          Are you sure you want to On Hold this seller?
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body2"
          sx={{
            fontSize: "16px",
            color: "#000000",
            mb: 3,
          }}
        >
          If you would like to place this seller on hold, please fill out the following:
        </Typography>

        {/* Form Container */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px", mb: "24px" }}>
          {/* Reminder Type Select */}
          <FormControl variant="standard" fullWidth>
            <Select
              labelId="reminder-type"
              id="reminder-select"
              value={reason}
              onChange={handleChange}
              displayEmpty
              disableUnderline
              renderValue={(selected) => {
                if (selected === "" || selected === null || selected === undefined) {
                  return (
                    <Typography sx={{ color: "#000", fontWeight: 500, fontSize: "16px" }}>
                      Send Email Reminder (optional)
                    </Typography>
                  );
                }
                const found = state.reminderTypes?.find(
                  (item) => item.days === selected
                );
                return found ? found.reminder_type_desc : selected;
              }}
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "#000",
                pt: 1,
                pb: 0.5,
                borderBottom: "2px solid #3f51b5",
                "&:hover": {
                  borderBottom: "2px solid #3f51b5",
                },
                "& .MuiSelect-select": {
                  pb: 0.5,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxWidth: "896px",
                    borderBottomLeftRadius: "16px",
                    borderBottomRightRadius: "16px",
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
                    mt: 0,
                  },
                },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }}
            >
              {state.reminderTypes?.map((item) => (
                <MenuItem
                  key={item.reminder_type_id}
                  value={item.days}
                  sx={{
                    py: 1.5,
                    px: 2,
                    fontSize: "16px",
                    fontWeight: reason === item.days ? 500 : 400,
                    color: "#000",
                    backgroundColor:
                      reason === item.days ? "#f0f4ff" : "#fff",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#f0f4ff",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "#e8eeff",
                    },
                  }}
                >
                  <ListItemText
                    primary={item.reminder_type_desc}
                    primaryTypographyProps={{
                      fontSize: "16px",
                      fontWeight: reason === item.days ? 500 : 400,
                    }}
                  />
                  {reason === item.days && (
                    <ListItemIcon sx={{ minWidth: "auto", ml: 2 }}>
                      <CheckIcon sx={{ color: "#002345", fontSize: "1.2rem" }} />
                    </ListItemIcon>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Comments TextField */}
          <TextField
            variant="standard"
            fullWidth
            value={comment}
            onChange={handleCommentChange}
            placeholder="Add comments (optional)"
            sx={{
              "& .MuiInput-underline:before": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "1px solid #bbb",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "2px solid #3f51b5",
              },
              "& .MuiInput-input::placeholder": {
                color: "#000000",
                opacity: 1,
              },
            }}
            inputProps={{
              sx: {
                color: "#000",
                padding: ".6rem 0",
                fontSize: "16px",
              },
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt:'24px'
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: "24px",
              maxWidth:'157px',
              minWidth:'120px',
              p: '12px 32px',
              maxHeight:'42px',
              whiteSpace:'nowrap',
              fontSize: "16px",
              fontWeight: 700,
              textTransform: "none",
              borderColor: "#1460AA",
              color: "#1460AA",
              "&:hover": {
                backgroundColor: "transparent",
                borderColor: "#1460AA",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePlaceOnHold}
            sx={{
              borderRadius: "24px",
              maxWidth:'157px',
              minWidth:'120px',
              p: '12px 32px',
              maxHeight:'42px',
              whiteSpace:'nowrap',
              fontSize: "16px",
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "#1460AA",
              color: "#fff",
              boxShadow: "none",
        
              "&:hover": {
                backgroundColor: "#1460AA",
                boxShadow: "none",
              },
            }}
          >
            Place On Hold
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default PlaceOnHoldModal;
